import React, { useState, useEffect, useCallback } from 'react';
import { clientesApi } from '../lib/api';
import { formatEndereco, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Loader2, Users, History, UserPlus, FileText, Wrench, DollarSign } from 'lucide-react';

const emptyEndereco = {
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: ''
};

const emptyCliente = {
  nome: '',
  telefone: '',
  endereco: { ...emptyEndereco }
};

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [formData, setFormData] = useState({ ...emptyCliente });
  const [saving, setSaving] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadClientes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientesApi.list();
      setClientes(response.data);
    } catch (error) {
      console.error('Error loading clientes:', error);
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  const handleOpenForm = (cliente = null) => {
    if (cliente) {
      setSelectedCliente(cliente);
      setFormData({
        nome: cliente.nome,
        telefone: cliente.telefone,
        endereco: cliente.endereco || { ...emptyEndereco }
      });
    } else {
      setSelectedCliente(null);
      setFormData({ ...emptyCliente });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedCliente(null);
    setFormData({ ...emptyCliente });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedCliente) {
        await clientesApi.update(selectedCliente.id, formData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await clientesApi.create(formData);
        toast.success('Cliente criado com sucesso!');
      }
      handleCloseForm();
      loadClientes();
    } catch (error) {
      console.error('Error saving cliente:', error);
      toast.error('Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCliente) return;

    try {
      await clientesApi.delete(selectedCliente.id);
      toast.success('Cliente excluído com sucesso!');
      setShowDeleteDialog(false);
      setSelectedCliente(null);
      loadClientes();
    } catch (error) {
      console.error('Error deleting cliente:', error);
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleOpenTimeline = async (cliente) => {
    setSelectedCliente(cliente);
    setTimelineOpen(true);
    setTimelineLoading(true);
    setTimelineData(null);
    try {
      const r = await clientesApi.timeline(cliente.id);
      setTimelineData(r.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar timeline do cliente');
    } finally {
      setTimelineLoading(false);
    }
  };

  const filteredClientes = clientes.filter(cliente =>
    cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.telefone.includes(searchTerm)
  );

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Clientes
          </h1>
          <p className="text-gray-500 mt-1">
            Gerencie seus clientes
          </p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-cliente-btn">
          <Plus size={18} className="mr-2" />
          Novo Cliente
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <Users size={20} />
              Lista de Clientes ({filteredClientes.length})
            </CardTitle>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-cliente-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredClientes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
            </div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th>Endereço</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClientes.map((cliente) => (
                    <tr key={cliente.id} data-testid={`cliente-row-${cliente.id}`}>
                      <td className="font-medium">{cliente.nome}</td>
                      <td>{cliente.telefone}</td>
                      <td className="max-w-xs truncate">{formatEndereco(cliente.endereco)}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenTimeline(cliente)}
                            data-testid={`timeline-cliente-${cliente.id}`}
                            title="Ver linha do tempo"
                            className="text-emerald-700 hover:text-emerald-800"
                          >
                            <History size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(cliente)}
                            data-testid={`edit-cliente-${cliente.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCliente(cliente);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-cliente-${cliente.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selectedCliente ? 'Editar Cliente' : 'Novo Cliente'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do cliente
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="cliente-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  required
                  placeholder="(00) 00000-0000"
                  data-testid="cliente-telefone-input"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Endereço</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="rua">Rua</Label>
                  <Input
                    id="rua"
                    value={formData.endereco.rua}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, rua: e.target.value }
                    })}
                    data-testid="cliente-rua-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero">Número</Label>
                  <Input
                    id="numero"
                    value={formData.endereco.numero}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, numero: e.target.value }
                    })}
                    data-testid="cliente-numero-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={formData.endereco.complemento}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, complemento: e.target.value }
                    })}
                    data-testid="cliente-complemento-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.endereco.bairro}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, bairro: e.target.value }
                    })}
                    data-testid="cliente-bairro-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.endereco.cidade}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, cidade: e.target.value }
                    })}
                    data-testid="cliente-cidade-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Input
                    id="estado"
                    value={formData.endereco.estado}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, estado: e.target.value }
                    })}
                    placeholder="SP"
                    maxLength={2}
                    data-testid="cliente-estado-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    value={formData.endereco.cep}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, cep: e.target.value }
                    })}
                    placeholder="00000-000"
                    data-testid="cliente-cep-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-cliente-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={timelineOpen} onOpenChange={setTimelineOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <History size={18} className="text-emerald-600" />
              Linha do tempo — {selectedCliente?.nome}
            </DialogTitle>
            <DialogDescription>
              Trajetória completa: Lead → Orçamento → OS → Financeiro
            </DialogDescription>
          </DialogHeader>

          {timelineLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : !timelineData ? (
            <div className="text-center py-6 text-gray-500">Sem dados para exibir</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <div className="flex items-center gap-2 text-blue-700"><UserPlus size={16} /><span className="text-xs font-medium">Leads</span></div>
                  <p className="text-2xl font-bold text-blue-900">{timelineData.resumo.leads}</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                  <div className="flex items-center gap-2 text-amber-700"><FileText size={16} /><span className="text-xs font-medium">Orçamentos</span></div>
                  <p className="text-2xl font-bold text-amber-900">{timelineData.resumo.orcamentos}</p>
                </div>
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                  <div className="flex items-center gap-2 text-emerald-700"><Wrench size={16} /><span className="text-xs font-medium">OS</span></div>
                  <p className="text-2xl font-bold text-emerald-900">{timelineData.resumo.ordens}</p>
                </div>
                <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
                  <div className="flex items-center gap-2 text-purple-700"><DollarSign size={16} /><span className="text-xs font-medium">Financeiro</span></div>
                  <p className="text-2xl font-bold text-purple-900">{timelineData.resumo.movimentos_financeiros}</p>
                </div>
              </div>

              {timelineData.eventos.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border rounded-lg">Nenhum evento registrado ainda para este cliente.</div>
              ) : (
                <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
                  {timelineData.eventos.map((ev, idx) => {
                    const color = ev.tipo.startsWith('lead') ? 'bg-blue-500'
                      : ev.tipo.startsWith('orcamento') ? 'bg-amber-500'
                      : ev.tipo.startsWith('os') ? 'bg-emerald-500'
                      : 'bg-purple-500';
                    return (
                      <div key={idx} className="relative" data-testid={`timeline-event-${idx}`}>
                        <span className={`absolute -left-[29px] top-1 w-3 h-3 rounded-full ${color} ring-4 ring-white`} />
                        <div className="rounded-md border bg-white p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-gray-800 text-sm">{ev.titulo}</p>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {ev.data ? formatDate(ev.data.slice(0, 10)) : '—'}
                            </span>
                          </div>
                          {ev.descricao && <p className="text-xs text-gray-600 mt-1">{ev.descricao}</p>}
                          {ev.status && <p className="text-xs text-gray-500 mt-1">Status: {ev.status}</p>}
                          {ev.autor && <p className="text-[10px] text-gray-400 mt-1">por {ev.autor}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente "{selectedCliente?.nome}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clientes;
